/**
 * Page /grc/referendums — back-office « Référendums » (GRC, migration 117).
 *
 * La mairie (admin + manager GRC) crée des consultations, les publie, suit
 * l'avancement EN TEMPS RÉEL (privilège staff), puis les clôture — ce qui publie
 * les résultats côté habitants. Les administrés ne voient les chiffres qu'à la
 * clôture (secret du scrutin, géré côté serveur).
 *
 * Conforme au guide des briques : GrcLayout (AppHeader + GrcNav), zéro
 * window.confirm (useConfirmDialog + {dialogElement}).
 */

import { useEffect, useState, useCallback } from 'react';
import { GrcLayout } from '@/components/grc/GrcLayout';
import { Card, Button, Badge, Alert, SectionEyebrow, Input, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { IdeesManager } from '@/components/grc/IdeesManager';
import { api, ApiException } from '@/lib/api';
import type { Referendum } from '@/lib/api';
import {
  Vote, Plus, Pencil, Trash2, Megaphone, Lock, AlertCircle, X, CheckCircle2,
} from 'lucide-react';

const INPUT_CLS =
  'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg';

function statutMeta(s: Referendum['statut']): { label: string; variant: 'success' | 'neutral' | 'warning' } {
  if (s === 'open') return { label: 'Ouverte', variant: 'success' };
  if (s === 'closed') return { label: 'Clôturée', variant: 'neutral' };
  return { label: 'Brouillon', variant: 'warning' };
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('fr-FR', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris',
  });
}

export default function ReferendumsPage() {
  const { confirm, dialogElement } = useConfirmDialog();
  const [items, setItems] = useState<Referendum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [edit, setEdit] = useState<Referendum | 'new' | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.grc.referendums.list();
      setItems(res.referendums);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { void reload(); }, [reload]);

  async function publier(r: Referendum) {
    const ok = await confirm({ title: 'Publier cette consultation ?', description: 'Les habitants pourront voter dès l\'heure d\'ouverture.', confirmLabel: 'Publier' });
    if (!ok) return;
    try { await api.grc.referendums.publier(r.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function cloturer(r: Referendum) {
    const ok = await confirm({ title: 'Clôturer maintenant ?', description: 'Les résultats deviendront visibles par tous les habitants. Action définitive.', danger: true, confirmLabel: 'Clôturer' });
    if (!ok) return;
    try { await api.grc.referendums.cloturer(r.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function supprimer(r: Referendum) {
    const ok = await confirm({ title: 'Supprimer cette consultation ?', description: 'La consultation et ses votes seront effacés. Action irréversible.', danger: true, confirmLabel: 'Supprimer' });
    if (!ok) return;
    try { await api.grc.referendums.remove(r.id); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  return (
    <GrcLayout active="referendums">
      <main id="main-content" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8 animate-fade-in">
        <div className="flex items-start justify-between gap-3">
          <div>
            <SectionEyebrow as="div">Consultation citoyenne</SectionEyebrow>
            <h1 className="mt-1.5 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
              Référendums &amp; consultations
            </h1>
            <p className="mt-2 text-sm text-ink-muted">
              Un seul vote par habitant. Les résultats restent masqués jusqu'à la clôture. L'avancement
              ci-dessous n'est visible que par vous.
            </p>
          </div>
          <Button variant="primary" onClick={() => setEdit('new')}>
            <Plus className="h-4 w-4" /> Nouvelle
          </Button>
        </div>

        {error && <Alert variant="error" icon={AlertCircle} className="mt-4">{error}</Alert>}

        <div className="mt-6 space-y-3">
          {loading ? (
            <Card variant="glass" padded><p className="text-sm text-ink-muted">Chargement…</p></Card>
          ) : items.length === 0 ? (
            <Card variant="glass" padded>
              <p className="text-sm text-ink-muted">Aucune consultation. Créez-en une pour recueillir l'avis des habitants.</p>
            </Card>
          ) : (
            items.map((r) => {
              const sm = statutMeta(r.statut);
              return (
                <Card key={r.id} variant="glass" padded>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={sm.variant} size="sm">{sm.label}</Badge>
                    {r.mode === 'indicatif' ? (
                      <Badge variant="neutral" size="sm">Mode indicatif</Badge>
                    ) : (
                      <Badge variant="grc" size="sm">Scrutin authentifié</Badge>
                    )}
                    <span className="ml-auto text-xs text-ink-dim tabular-nums">
                      {r.statut === 'draft'
                        ? 'Non publiée'
                        : `${r.totalVotants} votant${r.totalVotants > 1 ? 's' : ''} · clôture ${fmtDate(r.clotureAt)}`}
                    </span>
                  </div>

                  <h2 className="mt-2 font-display text-base font-semibold text-ink">{r.question}</h2>
                  {r.description && <p className="mt-1 text-sm text-ink-muted">{r.description}</p>}

                  {/* Avancement (staff only) */}
                  <div className="mt-3 space-y-2">
                    {r.options.map((o) => {
                      const voix = o.voix ?? 0;
                      const pct = r.totalVotants > 0 ? Math.round((voix / r.totalVotants) * 100) : 0;
                      return (
                        <div key={o.id} className="flex items-center gap-3">
                          <span className="w-40 shrink-0 truncate text-sm text-ink">{o.libelle}</span>
                          <div className="relative h-2 flex-1 overflow-hidden rounded-r-pill bg-bg-2">
                            <div className="absolute inset-y-0 left-0 rounded-r-pill bg-grc" style={{ width: `${pct}%` }} />
                          </div>
                          <span className="w-16 text-right text-xs font-medium text-grc-fg tabular-nums">{pct}% ({voix})</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {r.statut === 'draft' && (
                      <>
                        <Button variant="primary" size="sm" onClick={() => publier(r)}>
                          <Megaphone className="h-4 w-4" /> Publier
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setEdit(r)}>
                          <Pencil className="h-4 w-4" /> Modifier
                        </Button>
                      </>
                    )}
                    {r.statut === 'open' && (
                      <Button variant="ghost" size="sm" onClick={() => cloturer(r)}>
                        <Lock className="h-4 w-4" /> Clôturer maintenant
                      </Button>
                    )}
                    {r.statut === 'closed' && (
                      <span className="inline-flex items-center gap-1.5 text-sm text-success">
                        <CheckCircle2 className="h-4 w-4" /> Résultats publiés
                      </span>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => supprimer(r)} className="ml-auto text-ink-muted hover:text-urgent">
                      <Trash2 className="h-4 w-4" /> Supprimer
                    </Button>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* ===== Boîte à idées citoyenne (migration 130) ===== */}
        <div className="mt-10">
          <SectionEyebrow as="div">Participation</SectionEyebrow>
          <h2 className="mt-1.5 font-display text-lg font-semibold tracking-r-tight text-ink sm:text-xl">
            Boîte à idées
          </h2>
          <p className="mt-2 text-sm text-ink-muted">
            Les idées proposées par les habitants. Répondez-leur et faites-les avancer ; l'auteur est notifié.
          </p>
          <div className="mt-4">
            <IdeesManager />
          </div>
        </div>
      </main>

      {edit && (
        <ReferendumModal
          initial={edit === 'new' ? null : edit}
          onClose={() => setEdit(null)}
          onSaved={() => { setEdit(null); void reload(); }}
          onError={setError}
        />
      )}

      {dialogElement}
    </GrcLayout>
  );
}

// ============================================================================
// Modale — création / édition (brouillon uniquement)
// ============================================================================

function isoToLocalInput(iso: string): string {
  // ISO → valeur datetime-local (heure murale Europe/Paris).
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

function ReferendumModal({
  initial, onClose, onSaved, onError,
}: {
  initial: Referendum | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (m: string) => void;
}) {
  const [question, setQuestion] = useState(initial?.question ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [ouverture, setOuverture] = useState(initial ? isoToLocalInput(initial.ouvertureAt) : '');
  const [cloture, setCloture] = useState(initial ? isoToLocalInput(initial.clotureAt) : '');
  const [options, setOptions] = useState<string[]>(initial?.options.map((o) => o.libelle) ?? ['', '']);
  const [mode, setMode] = useState<'indicatif' | 'officiel'>(initial?.mode ?? 'indicatif');
  const [fcActif, setFcActif] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const s = await api.scolaire.facturation.integrationsStatut();
        if (alive) setFcActif(!!s.franceConnect);
      } catch { /* non bloquant : officiel restera désactivé */ }
    })();
    return () => { alive = false; };
  }, []);

  function setOpt(i: number, v: string) { setOptions((p) => p.map((x, j) => (j === i ? v : x))); }
  function addOpt() { setOptions((p) => [...p, '']); }
  function removeOpt(i: number) { setOptions((p) => (p.length <= 2 ? p : p.filter((_, j) => j !== i))); }

  async function save() {
    const opts = options.map((o) => o.trim()).filter(Boolean);
    if (question.trim().length < 3) { onError('La question est requise'); return; }
    if (opts.length < 2) { onError('Au moins 2 options'); return; }
    if (!ouverture || !cloture) { onError('Dates d\'ouverture et de clôture requises'); return; }
    const ouvertureAt = new Date(ouverture).toISOString();
    const clotureAt = new Date(cloture).toISOString();
    if (new Date(clotureAt).getTime() <= new Date(ouvertureAt).getTime()) {
      onError('La clôture doit être postérieure à l\'ouverture'); return;
    }
    setBusy(true);
    const body = { question: question.trim(), description: description.trim() || null, ouvertureAt, clotureAt, options: opts, mode };
    try {
      if (initial) await api.grc.referendums.update(initial.id, body);
      else await api.grc.referendums.create(body);
      onSaved();
    } catch (e) {
      onError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setBusy(false); }
  }

  return (
    <Dialog
      open onClose={onClose}
      title={initial ? 'Modifier la consultation' : 'Nouvelle consultation'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button variant="primary" onClick={save} disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button>
        </div>
      }
    >
      <div className="space-y-4">
        <Field label="Question posée aux habitants" htmlFor="ref-q" required>
          <Input id="ref-q" value={question} onChange={(e) => setQuestion(e.target.value)}
            placeholder="Réaménagement de la place : quelle option préférez-vous ?" />
        </Field>
        <Field label="Description (optionnelle)" htmlFor="ref-desc">
          <textarea id="ref-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2}
            className="w-full rounded-r-lg border border-border bg-surface/60 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-grc focus:ring-offset-2 focus:ring-offset-bg"
            placeholder="Contexte, enjeux…" />
        </Field>

        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Options de réponse</label>
          <div className="space-y-2">
            {options.map((o, i) => (
              <div key={i} className="flex gap-2">
                <Input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} />
                <button type="button" aria-label="Retirer" onClick={() => removeOpt(i)}
                  disabled={options.length <= 2}
                  className="rounded-r-md p-2 text-ink-dim hover:text-urgent disabled:opacity-30">
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
          <Button variant="secondary" size="sm" onClick={addOpt} className="mt-2">
            <Plus className="h-4 w-4" /> Ajouter une option
          </Button>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Ouverture du vote" htmlFor="ref-ouv" required>
            <input id="ref-ouv" type="datetime-local" value={ouverture}
              onChange={(e) => setOuverture(e.target.value)} className={INPUT_CLS} />
          </Field>
          <Field label="Clôture du vote" htmlFor="ref-clo" required>
            <input id="ref-clo" type="datetime-local" value={cloture}
              onChange={(e) => setCloture(e.target.value)} className={INPUT_CLS} />
          </Field>
        </div>

        <div>
          <label className="block text-sm font-medium text-ink mb-1.5">Type de consultation</label>
          <div className="space-y-2">
            <label className="flex items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 cursor-pointer">
              <input type="radio" name="ref-mode" className="mt-1"
                checked={mode === 'indicatif'} onChange={() => setMode('indicatif')} />
              <span>
                <span className="block text-sm font-medium text-ink">Indicatif — ouvert à tous</span>
                <span className="block text-xs text-ink-dim">
                  Tout habitant connecté peut voter (un compte = un vote). Idéal pour un sondage ou un avis.
                </span>
              </span>
            </label>
            <label className={`flex items-start gap-3 rounded-lg border border-border bg-surface/40 p-3 ${fcActif ? 'cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}>
              <input type="radio" name="ref-mode" className="mt-1" disabled={!fcActif}
                checked={mode === 'officiel'} onChange={() => setMode('officiel')} />
              <span>
                <span className="block text-sm font-medium text-ink">Officiel — identité vérifiée par FranceConnect</span>
                <span className="block text-xs text-ink-dim">
                  Chaque votant prouve son identité via FranceConnect (une personne = un vote). Pour les décisions à enjeu.
                  {!fcActif && ' — Indisponible : FranceConnect n\'est pas activé sur cette collectivité.'}
                </span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
